import { z } from "zod";
import { TOPIC_MAX_LENGTH } from "./constants";

export const presenterRefSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("member"), id: z.string().min(1) }),
  z.object({ kind: z.literal("guest"), id: z.string().min(1) }),
  z.object({ kind: z.literal("free"), name: z.string().trim().min(2, "Type the presenter's name") }),
]);

export const scheduleInputSchema = z.object({
  presenter: presenterRefSchema.nullable().refine((v) => v !== null, { message: "Choose a presenter" }),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Choose a date"),
  categoryId: z.string().min(1, "Choose a category"),
  topic: z.string().trim().max(TOPIC_MAX_LENGTH, `Keep it under ${TOPIC_MAX_LENGTH} characters`),
});
export type ScheduleInput = z.infer<typeof scheduleInputSchema>;

export const topicInputSchema = z.object({
  categoryId: z.string().min(1, "Choose a category"),
  topic: z.string().trim().max(TOPIC_MAX_LENGTH, `Keep it under ${TOPIC_MAX_LENGTH} characters`),
});
export type TopicInput = z.infer<typeof topicInputSchema>;

export const summaryInputSchema = z.object({
  categoryId: z.string().min(1, "Choose a category"),
  topic: z.string().trim().min(3, "Add the topic title").max(140, "Keep the title under 140 characters"),
  summary: z.string().trim().min(20, "Write a short summary (at least 20 characters)"),
  keyTakeaways: z.array(z.string().trim().max(200, "Keep each takeaway under 200 characters")),
  references: z.array(
    z.object({
      label: z.string().trim().max(120),
      url: z.string().trim().url("Enter a valid link, starting with https://"),
    }),
  ),
  notes: z.string().trim().max(2000, "Keep notes under 2000 characters"),
});
export type SummaryInput = z.infer<typeof summaryInputSchema>;

export const newPasswordSchema = z
  .string()
  .min(8, "Use at least 8 characters")
  .regex(/[a-z]/, "Add a lowercase letter")
  .regex(/[A-Z]/, "Add an uppercase letter")
  .regex(/[0-9]/, "Add a number");

export const passwordChangeSchema = z
  .object({
    current: z.string().min(1, "Enter your current password"),
    next: newPasswordSchema,
    confirm: z.string(),
  })
  .refine((v) => v.next === v.confirm, { path: ["confirm"], message: "Passwords don't match" });

export const memberInputSchema = z.object({
  name: z.string().trim().min(3, "Enter the full name"),
  nickname: z.string().trim().min(2, "Enter a display name").max(20, "Keep it under 20 characters"),
  role: z.enum(["member", "system_manager", "guest"]),
  email: z.union([z.literal(""), z.string().trim().email("Enter a valid email")]),
  position: z.string().trim().max(80),
});
export type MemberInput = z.infer<typeof memberInputSchema>;

export const profileInputSchema = z.object({
  nickname: z.string().trim().min(2, "Enter a display name").max(20, "Keep it under 20 characters"),
  email: z.union([z.literal(""), z.string().trim().email("Enter a valid email")]),
  position: z.string().trim().max(80, "Keep it under 80 characters"),
});
export type ProfileInput = z.infer<typeof profileInputSchema>;

export const settingsSchema = z
  .object({
    weekday: z.number().int().min(0).max(6),
    startTime: z.string().regex(/^\d{2}:\d{2}$/, "Use HH:MM"),
    endTime: z.string().regex(/^\d{2}:\d{2}$/, "Use HH:MM"),
    location: z.string().trim().max(120),
    reminders: z.object({ h7: z.boolean(), h1: z.boolean(), dday: z.boolean() }),
  })
  .refine((v) => v.startTime < v.endTime, { path: ["endTime"], message: "End time must be after start time" });

/** Flatten a ZodError into `{ "references.0.url": "message" }`. */
export function toFieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path.join(".") || "_form";
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}

export type ValidationResult<T> = { ok: true; data: T } | { ok: false; errors: Record<string, string> };

export function validate<T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, value: unknown): ValidationResult<T> {
  const result = schema.safeParse(value);
  if (result.success) return { ok: true, data: result.data };
  return { ok: false, errors: toFieldErrors(result.error) };
}
